import {
  Box, ButtonGroup, Flex, HStack, Icon,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { object as yupObject, string as yupString } from "yup";
import ProceedCancelButtons from "components/Button/ProceedCancelButtons";
import UserOrChannelSelectField from "components/FormElements/Fields/SelectFields/UserOrChannelSelectField";
import { fromCompositeId, toCompositeId } from "helpers/compositeId";
import RichTextAreaField from "components/FormElements/Fields/RichTextAreaField";
import { useCurrentUserQuery } from "features/User/hooks";
import RedirectToSlackMessageOauthForm from "components/Forms/RedirectToSlackMessageOauthForm";
import { useState } from "react";
import { CheckIcon } from "@chakra-ui/icons";
import { Text12 } from "components/Text";
import { useSendMessage } from "../hooks";

const MESSAGE_RECIPIENT_FIELD = {
  label: "To",
  name: "recipientCompositeId",
};

const MESSAGE_FIELD = {
  label: "Message",
  name: "message",
};

const sendMessageSchema = yupObject({
  recipientCompositeId: yupString().label(MESSAGE_RECIPIENT_FIELD.label).required(),
  message: yupString().label(MESSAGE_FIELD.label).required(),
});

interface TransformDataArgs {
  recipientCompositeId: string;
  message: string;
  taskIds?: string[];
}

function transformData(data: TransformDataArgs) {
  const { recipientCompositeId, taskIds, ...rest } = data;
  const { resourceType, resourceId } = fromCompositeId(recipientCompositeId);

  return {
    recipientType: resourceType,
    recipientId: resourceId,
    taskIds,
    ...rest,
  };
}

interface SendMessageFormModalProps {
  draftMessage?: string;
  targetId: string;
  targetType: string;
  onClose: () => void;
  onSuccess?: () => void;
  taskIds?: string[];
}

export function SendMessageModalForm({
  draftMessage = "",
  targetType,
  targetId,
  onClose,
  onSuccess = undefined,
  taskIds,
}: SendMessageFormModalProps) {
  // staletime is 0 so always refetches - could optimize more, but not going to worry about it
  const { data: currentUser } = useCurrentUserQuery({
    staleTime: 0,
  });
  const target = targetId && targetType ? { recipientCompositeId: toCompositeId(targetType, targetId) } : {};
  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(sendMessageSchema),
    values: { ...target, message: draftMessage, taskIds },
  });

  /* HandleOnSubmit */
  const { mutate: onSubmit } = useSendMessage({ transformData, onSuccess, onClose });

  if (currentUser && !currentUser?.sendMessageAuthorized) {
    return <RedirectToSlackMessageOauthForm />;
  }

  return (
    <Box
      border="1px solid"
      borderColor="gray.200"
      borderRadius="lg"
      bg="creamBg.500"
      p={4}
    >
      <Flex direction="column" gap={3}>
        <UserOrChannelSelectField
          {...MESSAGE_RECIPIENT_FIELD}
          control={control}
          errors={errors}
          variant="outline"
          formFieldProps={{ overrideHelperSpace: true }}
          types={["users", "channels"]}
          inlineLabel="To"
          components={{ DropdownIndicator: null, ClearIndicator: () => null }}
        />
        <RichTextAreaField
          {...MESSAGE_FIELD}
          hideToolbar={false}
          alwaysShowToolbar
          isEditable
          errors={errors}
          control={control}
          editorBgColor="white"
        />
        <ButtonGroup display="flex" justifyContent="flex-end">
          <ProceedCancelButtons handleSubmit={handleSubmit(onSubmit)} onClose={onClose} proceedText="Send" />
        </ButtonGroup>
      </Flex>
    </Box>
  );
}

const slideUpFade = keyframes`
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

interface SendMessageFormProps {
  draftMessage?: string;
  targetId: string;
  targetType: string;
  onClose?: () => void;
  onSuccess?: () => void;
  taskIds?: string[];
}

export default function SendMessageForm({
  draftMessage = "",
  targetType,
  targetId,
  onClose,
  onSuccess = undefined,
  taskIds,
}: SendMessageFormProps) {
  const [messageSent, setMessageSent] = useState(false);
  // staletime is 0 so always refetches - could optimize more, but not going to worry about it
  const { data: currentUser } = useCurrentUserQuery({
    staleTime: 0,
  });
  const target = targetId && targetType ? { recipientCompositeId: toCompositeId(targetType, targetId) } : {};
  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(sendMessageSchema),
    values: { ...target, message: draftMessage, taskIds },
  });

  /* HandleOnSubmit */
  const { mutate: onSubmit } = useSendMessage({ transformData, onSuccess: () => { setMessageSent(true); onSuccess?.(); }, onClose });

  if (currentUser && !currentUser?.sendMessageAuthorized) {
    return <RedirectToSlackMessageOauthForm />;
  }

  if (messageSent) {
    return (
      <HStack animation={`${slideUpFade} 0.3s ease-out`}>
        <Icon as={CheckIcon} />
        <Text12>Message sent!</Text12>
      </HStack>
    );
  }

  return (
    <Box
      border="1px solid"
      borderColor="gray.200"
      borderRadius="lg"
      bg="creamBg.500"
      p={4}
    >
      <Flex direction="column" gap={3}>
        <UserOrChannelSelectField
          {...MESSAGE_RECIPIENT_FIELD}
          control={control}
          errors={errors}
          variant="outline"
          formFieldProps={{ overrideHelperSpace: true }}
          types={["users", "channels"]}
          inlineLabel="To"
          components={{ DropdownIndicator: null, ClearIndicator: () => null }}
        />
        <RichTextAreaField
          {...MESSAGE_FIELD}
          hideToolbar={false}
          alwaysShowToolbar
          isEditable
          errors={errors}
          control={control}
          editorBgColor="white"
        />
        <ButtonGroup display="flex" justifyContent="flex-end">
          <ProceedCancelButtons handleSubmit={handleSubmit(onSubmit)} onClose={onClose} proceedText="Send" />
        </ButtonGroup>
      </Flex>
    </Box>
  );
}
